/**
 * AppShell — the authenticated chrome wrapping every in-app route.
 *
 * Layout responsibilities:
 *   - Top bar: brand mark + ScopeBar (Org/Site) + theme toggle + account.
 *   - Left rail at `lg`+, bottom thumb-rail below `lg` (same nav model).
 *   - A single scrollable <main>. The PAGE never scrolls horizontally; wide
 *     content scrolls inside its own container (DataGrid/DiffView) — hard rule.
 *   - Bottom padding on tablet so the thumb-rail never covers content.
 *
 * Rendered via React Router's <Outlet/>; the router guards auth before this
 * mounts (see routes/RequireAuth).
 */
import { Outlet } from "react-router-dom";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ScopeBar } from "./ScopeBar";
import { ThumbRailNav } from "./ThumbRailNav";
import { DomainRail } from "./DomainRail";
import { CommandPalette } from "./CommandPalette";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { AccountMenu } from "./AccountMenu";

export function AppShell() {
  return (
    // Tooltip.Provider once at the top so RoleGate tooltips share timing config.
    <Tooltip.Provider delayDuration={300}>
      <div className="flex min-h-dvh flex-col bg-ground">
        {/* Top bar */}
        <header className="sticky top-0 z-rail flex h-touch items-center gap-3 border-b border-line bg-surface px-3 sm:h-16 sm:px-4">
          <div className="flex items-center gap-2 pr-2">
            <BrandMark />
            <span className="hidden text-sm font-semibold text-ink sm:inline">Meridian</span>
          </div>
          <div className="min-w-0 flex-1 overflow-x-auto">
            <ScopeBar />
          </div>
          <ThemeToggle />
          <AccountMenu />
        </header>

        {/* Body: rail + main */}
        <div className="flex flex-1">
          <DomainRail />
          <main
            // Bottom padding clears the thumb-rail on tablet; none on desktop.
            className="min-w-0 flex-1 overflow-x-hidden px-3 pb-[calc(var(--md-thumb-rail)+16px)] pt-4 sm:px-6 lg:pb-6"
          >
            <Outlet />
          </main>
        </div>

        <ThumbRailNav />
        {/* Global ⌘K / Ctrl-K launcher (navigation only). */}
        <CommandPalette />
      </div>
    </Tooltip.Provider>
  );
}

/** Original Meridian brand mark: a violet "meridian" apex. No OEM logo. */
function BrandMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden>
      <rect width="24" height="24" rx="6" className="fill-brand" />
      <path
        d="M5 17 12 6l7 11"
        fill="none"
        className="stroke-ink-invert"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
